import React from 'react';
import { Box, TextField, Button, Alert, Fade, InputAdornment } from '@mui/material';
import { Email } from '@mui/icons-material';
import { LoginFormProps } from '../../shared/types';
import { useThemeMode } from '../../../../contexts/ThemeProvider';

const LoginWithOtpForm = ({ authLogic, config }: LoginFormProps) => {
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  
  const { formData, errors, otpSent, isLoading, updateFormData } = authLogic;

  const primaryColor = config?.theme?.primary || '#002465';

  const textFieldStyles = {
    input: { color: isDark ? '#ffffff' : 'black' },
    mb: 3,
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: isDark ? '#ffffff' : '#d1d5db'
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: isDark ? '#ffffff' : primaryColor,
        borderWidth: 2
      }
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: isDark ? 'rgba(255, 255, 255, 0.3)' : '#e5e7eb'
    },
    '& .MuiInputLabel-root': {
      color: isDark ? 'rgba(255,255,255,0.7)' : '#6b7280',
      '&.Mui-focused': {
        color: isDark ? '#ffffff' : primaryColor
      }
    },
    '& .MuiFormHelperText-root': {
      color: '#f87171'
    }
  };

  return (
    <Box>
      {!otpSent ? (
        <TextField
          fullWidth
          label="Email address"
          type="email"
          name="email"
          autoComplete="email"
          value={formData.emailWithOtp}
          onChange={e => updateFormData('emailWithOtp', e.target.value)}
          error={!!errors.email}
          helperText={errors.email}
          placeholder="Enter your email"
          disabled={isLoading}
          sx={textFieldStyles}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Email sx={{ color: isDark ? 'rgba(255,255,255,0.7)' : '#9ca3af', fontSize: 20 }} />
              </InputAdornment>
            )
          }}
        />
      ) : (
        <Fade in={otpSent}>
          <Box>
            <Alert 
              severity="success" 
              sx={{ 
                mb: 3, 
                borderRadius: 2,
                backgroundColor: isDark ? 'rgba(76, 175, 80, 0.15)' : undefined,
                color: isDark ? '#ffffff' : undefined,
                border: isDark ? '1px solid rgba(76, 175, 80, 0.3)' : undefined,
                '& .MuiAlert-icon': { 
                  color: isDark ? '#4caf50' : undefined 
                }
              }}
            >
              OTP sent to {formData.emailWithOtp}
            </Alert>
            <TextField
              fullWidth
              label="Enter 6-digit OTP"
              name="otp"
              autoComplete="one-time-code"
              value={formData.otp}
              onChange={e => updateFormData('otp', e.target.value)}
              error={!!errors.otp}
              helperText={errors.otp}
              disabled={isLoading}
              inputProps={{
                maxLength: 6,
                style: { textAlign: 'center', fontSize: '1.2rem', letterSpacing: '0.5rem' }
              }}
              sx={{
                ...textFieldStyles,
                mb: 4
              }}
            />
          </Box>
        </Fade>
      )}

      <Button
        type="submit"
        fullWidth
        variant="contained"
        size="large"
        disabled={isLoading}
        sx={{
          py: 2,
          fontSize: '1rem',
          fontWeight: 600,
          borderRadius: 2,
          textTransform: 'none',
          backgroundColor: isDark ? '#ffffff' : primaryColor,
          color: isDark ? '#000000' : '#ffffff',
          boxShadow: isDark 
            ? '0 4px 12px rgba(255, 255, 255, 0.2)' 
            : `0 4px 12px rgba(0, 36, 101, 0.2)`,
          cursor: isLoading ? 'not-allowed' : 'pointer',
          opacity: isLoading ? 0.7 : 1,
          '&:hover': {
            backgroundColor: isDark 
              ? (isLoading ? '#ffffff' : 'rgba(255, 255, 255, 0.9)') 
              : (isLoading ? primaryColor : primaryColor),
            filter: isLoading ? 'none' : 'brightness(0.9)',
            boxShadow: isLoading 
              ? (isDark ? '0 4px 12px rgba(255, 255, 255, 0.2)' : `0 4px 12px rgba(0, 36, 101, 0.2)`)
              : (isDark ? '0 6px 16px rgba(255, 255, 255, 0.25)' : `0 6px 16px rgba(0, 36, 101, 0.25)`)
          },
          '&:disabled': {
            backgroundColor: isDark ? '#ffffff' : primaryColor,
            color: isDark ? '#000000' : 'white'
          }
        }}
      >
        {isLoading ? (
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                width: 20,
                height: 20,
                border: isDark 
                  ? '2px solid rgba(0,0,0,0.3)' 
                  : '2px solid rgba(255,255,255,0.3)',
                borderTop: isDark 
                  ? '2px solid black' 
                  : '2px solid white',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' }
                }
              }}
            />
            Loading...
          </Box>
        ) : otpSent ? (
          'Verify OTP'
        ) : (
          'Send OTP'
        )}
      </Button>
    </Box>
  );
};

export default LoginWithOtpForm;